/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import createFragment from 'react-addons-create-fragment';
import { formatPrice } from '../../../../helpers/formatNumbers';
import EditMeasurements from './EditMeasurementsConnector';
import OptionTooltip from './OptionTooltip';

const formatInches = (inches) => {
  const wholeInches = Math.floor(inches);
  const decimal = inches % 1;

  const fractions = {
    0.125: '1/8',
    0.25: '1/4',
    0.375: '3/8',
    0.5: '1/2',
    0.625: '5/8',
    0.75: '3/4',
    0.875: '7/8',
  };

  if (decimal > 0) {
    return `${wholeInches} ${fractions[decimal]}"`;
  }

  return `${wholeInches}"`;
};

const formatAttributeValue = (attribute) => {
  if (attribute.name === 'Height' || attribute.name === 'Width') {
    return formatInches(attribute.value);
  }
  return attribute.value;
};

const formatExpandedChoiceValue = (value, price) => {
  const priceAppend = price > 0 ? ` (${formatPrice(price)})` : '';
  return `${value}${priceAppend}`;
};

const editMeasurementsRows = (itemId) => {
  const width = (
    <tr key={`${itemId}-editmeasurementsWidth`}>
      <td className="optionName" style={{ verticalAlign: 'middle' }}>Width</td>
      <td rowSpan={2} style={{ verticalAlign: 'middle' }}>
        <EditMeasurements itemId={itemId} />
      </td>
    </tr>
  );
  const height = (
    <tr key={`${itemId}-editmeasurementsHeight`}>
      <td className="optionName" style={{ verticalAlign: 'middle' }}>Height</td>
    </tr>
  );
  const rows = createFragment({
    width,
    height,
  });
  return rows;
};

const nonEditMeasurementsRows = (attributes, itemId) => {
  const width = Number(attributes.find(attribute => attribute.name === 'Width').value);
  const height = Number(attributes.find(attribute => attribute.name === 'Height').value);

  const widthCell = (
    <tr key={`${itemId}-editmeasurementsWidth`}>
      <td className="optionName" style={{ verticalAlign: 'middle' }}>Width</td>
      <td rowSpan={2} style={{ verticalAlign: 'middle' }}>
        <table>
          <OptionTooltip>
            <tbody>
              <tr key={`${itemId}-width`}>
                <td className="optionValue">{formatInches(width)}</td>
              </tr>
              <tr key={`${itemId}-height`}>
                <td className="optionValue">{formatInches(height)}</td>
              </tr>
            </tbody>
          </OptionTooltip>
        </table>
      </td>
    </tr>
  );
  const heightCell = (
    <tr key={`${itemId}-editmeasurementsHeight`}>
      <td className="optionName" style={{ verticalAlign: 'middle' }}>Height</td>
    </tr>
  );
  const rows = createFragment({
    widthCell,
    heightCell,
  });
  return rows;
};

const OptionSummary =
({
  itemId, attributes, brand, expandedOptions, isOpen, hasWidthAndHeight, isValid,
  isLockedOrOrdered, isMandi, isStockProduct,
}) => (
  <div className="optionSummary" key={`optionSummary-${itemId}`}>
    <table>
      <tbody>
        <tr data-product-option="brandName" className="brandName">
          <td className="optionName">Brand</td>
          <td className="optionValue">{brand}</td>
        </tr>
        {(!hasWidthAndHeight || !isValid) && attributes.map((attribute, idx) => (
          <tr key={`${itemId}-attribute-${idx}`}>
            <td className="optionName">{attribute.name}</td>
            <td className="optionValue">{formatAttributeValue(attribute)}</td>
          </tr>
        ))}
        {(hasWidthAndHeight && isValid) && attributes.map((attribute, idx) => (
          ((attribute.name !== 'Width' && attribute.name !== 'Height') || isLockedOrOrdered || isMandi) ? (
            <tr key={`${itemId}-attribute-${idx}`}>
              <td className="optionName">{attribute.name}{isLockedOrOrdered}</td>
              <td className="optionValue">{formatAttributeValue(attribute)}</td>
            </tr>
          ) : (
            attribute.name === 'Width' && (
              isStockProduct ?
              nonEditMeasurementsRows(attributes, itemId) :
              editMeasurementsRows(itemId)
            )
          )
        ))}
        {isOpen &&
          expandedOptions.map((option, idx) => (
            <tr key={`${itemId}-expanded-${idx}`}>
              <td className="optionName">{option.optionName}</td>
              <td className="optionValue">
                {formatExpandedChoiceValue(option.choiceValue, option.price)}
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

OptionSummary.propTypes = {
  itemId: PropTypes.string.isRequired,
  attributes: PropTypes.arrayOf(PropTypes.object).isRequired,
  brand: PropTypes.string.isRequired,
  expandedOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOpen: PropTypes.bool.isRequired,
  hasWidthAndHeight: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  isLockedOrOrdered: PropTypes.bool.isRequired,
  isMandi: PropTypes.bool.isRequired,
  isStockProduct: PropTypes.bool.isRequired,
};

export default OptionSummary;
